import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/about', label: 'About Us' },
    { href: '/retirement-planning', label: 'Retirement Planning' },
    { href: '/wealth-management', label: 'Wealth Management' },
    { href: '/financial-advisor', label: 'Financial Advisor' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (href) => {
    return location.pathname === href;
  };

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white shadow-lg py-2' 
          : 'bg-white/95 backdrop-blur-sm border-b border-gray-100 py-4'
      }`} 
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link 
            to="/" 
            className="group flex items-center gap-3" 
            data-testid="logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_finance-portal-143/artifacts/syg2bhjq_summit-investment-group-2-279w%20%281%29.webp" 
              alt="Summit Investment Group"
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link 
                key={link.href}
                to={link.href}
                className={`nav-link relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive(link.href) 
                    ? 'text-[#3d4f5f] bg-[#e8eef2]' 
                    : 'text-gray-600 hover:text-[#3d4f5f] hover:bg-gray-50'
                }`}
                data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Phone CTA */}
            <a 
              href="tel:+19184777227" 
              className="group flex items-center gap-2 ml-4 px-5 py-2.5 bg-[#3d4f5f] text-white rounded-lg font-semibold text-sm transition-all duration-200 hover:bg-[#2d3e4e] hover:shadow-lg hover:scale-105 active:scale-100"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4 transition-transform duration-200 group-hover:rotate-12" />
              <span>918.477.7227</span>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-[#3d4f5f] rounded-lg hover:bg-gray-100 transition-colors duration-200"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            <div className="relative w-6 h-6">
              <span className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'top-3 rotate-45' : 'top-1'}`}></span>
              <span className={`absolute left-0 top-3 w-6 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'top-3 -rotate-45' : 'top-5'}`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="pt-4 pb-2 space-y-1" data-testid="mobile-nav">
            {navLinks.map((link) => (
              <Link 
                key={link.href}
                to={link.href}
                className={`block px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  isActive(link.href) 
                    ? 'text-[#3d4f5f] bg-[#e8eef2]' 
                    : 'text-gray-600 hover:text-[#3d4f5f] hover:bg-gray-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Mobile Phone CTA */}
            <a 
              href="tel:+19184777227" 
              className="flex items-center justify-center gap-2 mx-4 mt-4 px-4 py-3 bg-[#3d4f5f] text-white rounded-lg font-semibold transition-all duration-200 hover:bg-[#2d3e4e]"
              data-testid="mobile-phone-link"
            >
              <Phone className="w-5 h-5" />
              <span>Call 918.477.7227</span>
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
